import { useAuthStore } from '@/features/auth';
import { useCreateConversation } from '@/features/conversation';
import type { Conversation } from '@/features/conversation/models/conversation';
import type { User } from '@/features/participant';
import i18next from 'i18next';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import type { Message } from '../models/message';
import { useMessageDelete } from '../queries/use-message-delete';
import { useMessageForward } from './use-message-forward';
import { useMessageForwardDialog } from './use-message-forward-dialog';

export const useMessageForwardDialogLogic = () => {
  const { user: currentUser } = useAuthStore();
  const { show, message, handleClose } = useMessageForwardDialog();
  const [searchQuery, setSearchQuery] = useState('');
  const [isForwarding, setIsForwarding] = useState(false);
  const [sentConversations, setSentConversations] = useState<Map<string, Message>>(new Map());
  const [sentUsers, setSentUsers] = useState<Map<string, Message>>(new Map());

  const { mutateAsync: forwardMessage } = useMessageForward();
  const { xoa: undoForward } = useMessageDelete();
  const { mutateAsync: createConversation } = useCreateConversation();

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleConversationSend = useCallback(
    async (conversation: Conversation) => {
      if (!message) return;

      try {
        setIsForwarding(true);
        const msg = await forwardMessage({
          messageId: message.messageId,
          conversationId: conversation.conversationId!,
          message: message
        });

        // Mark conversation as sent
        setSentConversations((prev) => new Map(prev).set(conversation.conversationId!, msg!));
      } catch (error) {
        console.error('Failed to forward message:', error);
      } finally {
        setIsForwarding(false);
      }
    },
    [message, forwardMessage]
  );

  const handleUserSend = useCallback(
    async (user: User) => {
      if (!message) return;

      try {
        setIsForwarding(true);
        const conv = await createConversation({
          conversationType: 'DIRECT',
          conversationName: message.senderName,
          participantIds: [currentUser!.user_id!, user.id],
          parentConversationId: undefined,
          metadata: undefined
        });

        const msg = await forwardMessage({
          messageId: message.messageId,
          conversationId: conv.conversationId!,
          message: message
        });

        // Mark user as sent for UI state management
        setSentUsers((prev) => new Map(prev).set(user.id, msg!));
      } catch (error) {
        console.error('Failed to forward message:', error);
      } finally {
        setIsForwarding(false);
      }
    },
    [message, createConversation, forwardMessage, currentUser]
  );

  const handleConversationUndo = useCallback(
    async (conversationId: string) => {
      try {
        const message = sentConversations.get(conversationId);
        if (!message) return;
        await undoForward({
          messageId: message.messageId,
          conversationId,
          bucketId: message!.bucketId,
          scope: 'Everyone'
        });

        // For now, just remove from sent conversations
        setSentConversations((prev) => {
          const newMap = new Map(prev);
          newMap.delete(conversationId);
          return newMap;
        });
      } catch (error) {
        console.error('Failed to undo forward:', error);
        toast.error(i18next.t('messages.forward.undoError'));
      }
    },
    [sentConversations]
  );

  const handleUserUndo = useCallback(
    async (userId: string) => {
      try {
        const message = sentUsers.get(userId);
        if (!message) return;

        await undoForward({
          messageId: message.messageId,
          conversationId: message.conversationId,
          bucketId: message.bucketId,
          scope: 'Everyone'
        });

        // For now, just remove from sent users
        setSentUsers((prev) => {
          const newMap = new Map(prev);
          newMap.delete(userId);
          return newMap;
        });
      } catch (error) {
        console.error('Failed to undo forward:', error);
        toast.error(i18next.t('messages.forward.undoError'));
      }
    },
    [sentUsers]
  );

  const handleDialogClose = useCallback(() => {
    if (isForwarding) return;
    const handleReset = () => {
      setSearchQuery('');
      setSentConversations(new Map());
      setSentUsers(new Map());
    };
    handleClose(handleReset);
  }, [isForwarding, handleClose]);

  return {
    // Dialog state
    show,
    message,
    isForwarding,

    // Search state
    searchQuery,
    handleSearchChange,

    // Conversation state and handlers
    sentConversations,
    handleConversationSend,
    handleConversationUndo,

    // User state and handlers
    sentUsers,
    handleUserSend,
    handleUserUndo,

    // Dialog handlers
    handleDialogClose
  };
};
